import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime, inr, STATUS_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [refundNote, setRefundNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/admin/orders/${id}`).then((r) => { setOrder(r.data); setRefundNote(r.data.refund_note || ""); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!order) return <p className="text-neutral-400 font-mono text-sm">LOADING…</p>;

  const act = async (fn, key) => {
    setBusy(key);
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const setStatus = (status) => act(() => api.post(`/admin/orders/${id}/status`, { status }), status);
  const ship = order.shiprocket || {};

  const printDoc = async (mode) => {
    try {
      const r = await api.get(`/admin/orders/${id}/invoice`, { params: { mode }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const nextActions = { placed: "packed", packed: "shipped", shipped: "delivered" };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-order-detail">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="admin-order-number">{order.order_number}</h1>
          <p className="text-sm text-neutral-500">{fmtDateTime(order.created_at)}</p>
        </div>
        <span className={`text-xs font-mono uppercase px-3 py-1.5 ${STATUS_COLORS[order.status]}`} data-testid="admin-order-status">{order.status}</span>
        <Button variant="outline" size="sm" onClick={() => printDoc("invoice")} data-testid="print-invoice-btn"><Printer size={15} className="mr-1" /> Invoice PDF</Button>
        <Button variant="outline" size="sm" onClick={() => printDoc("packing")} data-testid="packing-slip-btn"><Printer size={15} className="mr-1" /> Packing Slip</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <section className="bg-white border border-neutral-200 p-5">
            <h2 className="font-heading font-semibold mb-3">Items</h2>
            {order.items.map((i, idx) => (
              <div key={i.variant_id} className="flex gap-3 items-center border-b border-neutral-100 last:border-0 py-2.5 text-sm">
                <img src={i.image} alt="" className="h-12 w-12 object-cover border border-neutral-200" />
                <div className="flex-1">
                  <p className="font-medium">{i.title}</p>
                  <p className="text-xs font-mono text-neutral-500">{i.variant_label} · {i.sku} × {i.qty}</p>
                  {i.purity && (
                    <p className="text-[11px] font-mono text-neutral-400 mt-0.5">
                      {i.purity} {i.metal} · net {i.net_weight}g · ₹{Number(i.rate_per_gram || 0).toLocaleString("en-IN")}/g{i.hallmark ? ` · ${i.hallmark}` : ""}
                    </p>
                  )}
                  {i.personalisation?.summary && (
                    <p className="text-xs font-semibold text-brand mt-1" data-testid={`admin-pers-${i.sku}`}>✦ PERSONALISATION: {i.personalisation.summary}</p>
                  )}
                  {i.made_to_order && (
                    <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`production-${idx}`}>
                      <select value={i.production_stage || "placed"} disabled={!!busy}
                        onChange={(e) => act(() => api.post(`/admin/orders/${id}/lines/${idx}/stage`, { stage: e.target.value }), `stage-${idx}`)}
                        className="h-7 border border-neutral-300 rounded text-[11px] bg-white" data-testid={`stage-select-${idx}`}>
                        {["placed", "metal_procured", "in_production", "hallmarking", "quality_check", "dispatched"].map((s) => (
                          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                        ))}
                      </select>
                      <Input type="number" step="0.001" placeholder="actual g" defaultValue={i.actual_weight || ""} id={`aw-${idx}`}
                        className="h-7 w-24" data-testid={`actual-weight-${idx}`} />
                      <Button size="sm" variant="ghost" disabled={!!busy}
                        onClick={() => { const v = parseFloat(document.getElementById(`aw-${idx}`).value); if (v > 0) act(() => api.post(`/admin/orders/${id}/lines/${idx}/actual-weight`, { actual_weight: v }), `aw-${idx}`); }}
                        data-testid={`save-weight-${idx}`}>Set actual</Button>
                      {i.actual_weight && (
                        <span className="text-[10px] font-mono text-neutral-500" data-testid={`weight-variance-${idx}`}>
                          billing {i.breakup?.billing_weight ?? i.net_weight}g → actual {i.actual_weight}g ({i.weight_variance_g > 0 ? "+" : ""}{i.weight_variance_g}g) · price unchanged
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="font-medium">{inr(i.price * i.qty)}</span>
              </div>
            ))}
            <dl className="mt-4 space-y-1.5 text-sm border-t border-neutral-200 pt-3">
              <div className="flex justify-between"><dt className="text-neutral-500">Subtotal</dt><dd>{inr(order.subtotal)}</dd></div>
              {order.discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Discount ({order.coupon_code})</dt><dd>−{inr(order.discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-neutral-500">Shipping</dt><dd>{order.shipping_fee === 0 ? "FREE" : inr(order.shipping_fee)}</dd></div>
              {order.cod_fee > 0 && <div className="flex justify-between"><dt className="text-neutral-500">COD fee</dt><dd>{inr(order.cod_fee)}</dd></div>}
              <div className="flex justify-between text-xs text-neutral-500"><dt>Incl. GST</dt><dd>{inr(order.gst_amount)}</dd></div>
              <div className="flex justify-between font-heading font-bold border-t border-neutral-200 pt-2"><dt>Total</dt><dd>{inr(order.total)}</dd></div>
            </dl>
          </section>

          <section className="bg-white border border-neutral-200 p-5" data-testid="timeline-section">
            <h2 className="font-heading font-semibold mb-3">Timeline</h2>
            <ul className="space-y-2">
              {(order.timeline || []).map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="h-2 w-2 rounded-full bg-neutral-900 mt-1.5 shrink-0" />
                  <div>
                    <span className="font-mono uppercase text-xs font-semibold">{t.status}</span>
                    <span className="text-neutral-500 ml-2 text-xs">{fmtDateTime(t.at)}</span>
                    {t.note && <p className="text-neutral-500 text-xs">{t.note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="space-y-5">
          <section className="bg-white border border-neutral-200 p-5">
            <h2 className="font-heading font-semibold mb-3">Customer</h2>
            <p className="text-sm font-medium">{order.customer?.name}</p>
            <p className="text-sm text-neutral-500">{order.customer?.email}</p>
            <p className="text-sm font-mono text-neutral-500">{order.customer?.phone}</p>
            <div className="border-t border-neutral-200 mt-3 pt-3 text-sm text-neutral-600">
              <p>{order.address?.line1}{order.address?.line2 ? `, ${order.address.line2}` : ""}</p>
              <p>{order.address?.city} — {order.address?.pincode}, {order.address?.state}</p>
            </div>
            <p className="text-xs font-mono uppercase mt-3">
              {order.payment_method} · <span className={order.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}>{order.payment_status}</span>
            </p>
            {order.has_personalised && (
              <p className="text-xs mt-2 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5" data-testid="admin-personalised-note">
                Contains personalised piece(s) — made to order, non-returnable.
              </p>
            )}
            {order.gift?.wrap && (
              <p className="text-xs mt-2 bg-neutral-50 border border-neutral-200 px-2 py-1.5" data-testid="admin-gift-note">
                Gift wrap{order.gift.hide_price ? " · hide prices" : ""}{order.gift.message ? ` · “${order.gift.message}”` : ""}
              </p>
            )}
          </section>

          <section className="bg-white border border-neutral-200 p-5" data-testid="fulfilment-section">
            <h2 className="font-heading font-semibold mb-3">Fulfilment</h2>
            <div className="flex flex-wrap gap-2">
              {nextActions[order.status] && (
                <Button size="sm" className="bg-neutral-900 hover:bg-brand text-white btn-press" disabled={!!busy} onClick={() => setStatus(nextActions[order.status])} data-testid={`mark-${nextActions[order.status]}-btn`}>
                  Mark {nextActions[order.status]}
                </Button>
              )}
              {!["delivered", "cancelled"].includes(order.status) && (
                <Button size="sm" variant="outline" className="text-red-600" disabled={!!busy} onClick={() => act(() => api.post(`/admin/orders/${id}/cancel`), "cancel")} data-testid="admin-cancel-order-btn">
                  Cancel
                </Button>
              )}
            </div>

            <div className="border-t border-neutral-200 mt-4 pt-4">
              <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Shiprocket</p>
              {ship.shipment_id ? (
                <div className="text-sm space-y-1">
                  <p className="font-mono text-xs">Shipment: {ship.shipment_id}</p>
                  {ship.awb ? (
                    <>
                      <p className="font-mono text-xs" data-testid="admin-awb">AWB: {ship.awb} ({ship.courier})</p>
                      {order.tracking && <p className="text-emerald-700 text-xs font-medium">{order.tracking.status}</p>}
                      <Button size="sm" variant="outline" className="mt-2" disabled={!!busy} onClick={() => act(async () => { await api.get(`/admin/orders/${id}/tracking`); toast.success("Tracking refreshed"); }, "track")} data-testid="refresh-tracking-btn">
                        <RefreshCw size={13} className="mr-1" /> Refresh Tracking
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2" disabled={!!busy} onClick={() => act(() => api.post(`/admin/orders/${id}/awb`), "awb")} data-testid="generate-awb-btn">
                      Generate AWB
                    </Button>
                  )}
                </div>
              ) : (
                <Button size="sm" variant="outline" disabled={!!busy || order.status === "cancelled"} onClick={() => act(() => api.post(`/admin/orders/${id}/shipment`), "ship")} data-testid="create-shipment-btn">
                  Create Shipment
                </Button>
              )}
            </div>

            <div className="border-t border-neutral-200 mt-4 pt-4">
              <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Refund Note</p>
              <div className="flex gap-2">
                <Input value={refundNote} onChange={(e) => setRefundNote(e.target.value)} placeholder="e.g. Refunded via UPI" className="h-9" data-testid="refund-note-input" />
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(() => api.post(`/admin/orders/${id}/refund-note`, { note: refundNote }), "refund")} data-testid="save-refund-note-btn">
                  Save
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
